// Snooze time calculation utilities

export interface SnoozeOption {
  id: string
  label: string
  subLabel: string
  datetime: Date
}

/**
 * Round a date to the nearest 30-minute interval
 */
export function roundToNearest30Min(date: Date): Date {
  const result = new Date(date)
  const minutes = result.getMinutes()
  const roundedMinutes = Math.round(minutes / 30) * 30
  result.setMinutes(roundedMinutes, 0, 0)
  if (roundedMinutes === 60) {
    result.setHours(result.getHours() + 1)
    result.setMinutes(0)
  }
  return result
}

/**
 * Set specific time on a date
 */
export function setTime(date: Date, hours: number, minutes: number): Date {
  const result = new Date(date)
  result.setHours(hours, minutes, 0, 0)
  return result
}

/**
 * Add days to a date
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

/**
 * Add months to a date
 */
export function addMonths(date: Date, months: number): Date {
  const result = new Date(date)
  result.setMonth(result.getMonth() + months)
  return result
}

/**
 * Get "Later today" time - 18:30 or next hour if past 18:00
 */
export function getLaterTodayTime(): Date {
  const now = new Date()
  if (now.getHours() >= 18) {
    // Round up to next hour
    const result = new Date(now)
    result.setHours(result.getHours() + 1, 0, 0, 0)
    return result
  }
  return setTime(now, 18, 30)
}

/**
 * Get tomorrow at 09:00
 */
export function getTomorrowMorning(): Date {
  const tomorrow = addDays(new Date(), 1)
  return setTime(tomorrow, 9, 0)
}

/**
 * Get next Monday at 09:00 (skip to next week if today is Monday)
 */
export function getNextMonday(): Date {
  const now = new Date()
  const dayOfWeek = now.getDay() // 0 = Sunday, 1 = Monday, etc.
  const daysUntilMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 7 : (8 - dayOfWeek)
  const monday = addDays(now, daysUntilMonday)
  return setTime(monday, 9, 0)
}

/**
 * Get one week from now, rounded to nearest 30 min
 */
export function getOneWeekLater(): Date {
  const oneWeek = addDays(new Date(), 7)
  return roundToNearest30Min(oneWeek)
}

/**
 * Get one month from now at 09:00
 */
export function getOneMonthLater(): Date {
  const oneMonth = addMonths(new Date(), 1)
  return setTime(oneMonth, 9, 0)
}

/**
 * Format date as "Mon, Jan 6" or "Mon, Jan 6, 09:00"
 */
export function formatSnoozeDate(date: Date, includeTime = false): string {
  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

  const day = dayNames[date.getDay()]
  const month = monthNames[date.getMonth()]
  const dayNum = date.getDate()

  if (includeTime) {
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${day}, ${month} ${dayNum}, ${hours}:${minutes}`
  }

  return `${day}, ${month} ${dayNum}`
}

/**
 * Format time as "HH:mm"
 */
export function formatTime(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * Generate all preset snooze options
 */
export function getPresetOptions(): SnoozeOption[] {
  const laterToday = getLaterTodayTime()
  const tomorrow = getTomorrowMorning()
  const nextMonday = getNextMonday()
  const oneWeek = getOneWeekLater()
  const oneMonth = getOneMonthLater()

  return [
    {
      id: 'later_today',
      label: 'Mais tarde hoje',
      subLabel: formatTime(laterToday),
      datetime: laterToday,
    },
    {
      id: 'tomorrow',
      label: 'Amanhã',
      subLabel: `${formatSnoozeDate(tomorrow)}, 09:00`,
      datetime: tomorrow,
    },
    {
      id: 'next_monday',
      label: 'Próxima segunda',
      subLabel: `${formatSnoozeDate(nextMonday)}, 09:00`,
      datetime: nextMonday,
    },
    {
      id: 'one_week',
      label: 'Uma semana',
      subLabel: formatSnoozeDate(oneWeek, true),
      datetime: oneWeek,
    },
    {
      id: 'one_month',
      label: 'Um mês',
      subLabel: formatSnoozeDate(oneMonth, true),
      datetime: oneMonth,
    },
  ]
}

/**
 * Generate 30-minute time slots for the custom picker
 */
export function generateTimeSlots(): string[] {
  const slots: string[] = []
  for (let hour = 0; hour < 24; hour++) {
    for (const min of [0, 30]) {
      slots.push(
        `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`
      )
    }
  }
  return slots
}

/**
 * Get days in a month for calendar display
 */
export function getCalendarDays(year: number, month: number): { date: Date; isCurrentMonth: boolean }[] {
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const startDayOfWeek = firstDay.getDay()

  const days: { date: Date; isCurrentMonth: boolean }[] = []

  // Add days from previous month
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    const date = new Date(year, month, -i)
    days.push({ date, isCurrentMonth: false })
  }

  // Add days of current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), isCurrentMonth: true })
  }

  // Add days from next month to complete the grid (6 rows x 7 days = 42)
  const remaining = 42 - days.length
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false })
  }

  return days
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

/**
 * Check if a date is today
 */
export function isToday(date: Date): boolean {
  return isSameDay(date, new Date())
}

/**
 * Check if a date is in the past (before today)
 */
export function isPastDate(date: Date): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const compareDate = new Date(date)
  compareDate.setHours(0, 0, 0, 0)
  return compareDate < today
}

/**
 * Parse time string "HH:mm" and apply to a date
 */
export function applyTimeToDate(date: Date, timeString: string): Date {
  const [hours, minutes] = timeString.split(':').map(Number)
  const result = new Date(date)
  result.setHours(hours, minutes, 0, 0)
  return result
}
